// @flow

import React from 'react';
import type { Node } from 'react';
import styled, { css } from 'styled-components';

import { useImages } from 'hooks';
import { breakLine, flex, h242428, p18, topPadding, Width, widths } from 'styles';
import type { AllImages } from 'hooks/use-images';
import { ImageLoader } from 'components/Gui';

const Wrapper = styled.section`
  ${flex()};
  flex-direction: column;
  ${topPadding(48, 48, 94)};

  h2 {
    text-align: center;
    ${breakLine(false, true, true)};
    max-width: 420px;

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      max-width: 520px;
    }

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      max-width: 835px;
    }
  }
`;

const List = styled.div`
  ${flex()};
  flex-direction: column;
  ${topPadding(8, 48, 80)};
  width: 100%;
`;

const ListItem = styled.div`
  margin-top: 40px;
  ${flex()};
  flex-direction: column;
  max-width: 380px;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    flex-direction: row;
    justify-content: space-between;
    max-width: 720px;
    width: 100%;
    min-height: 340px;
    margin-top: 0;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    max-width: 800px;
    min-height: 420px;
  }
  .image {
    > div {
      ${({ widthArray }) =>
        css`
          ${widths(...widthArray)}
        `};
    }

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      width: 320px;
      ${flex()};
    }

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      width: 380px;
    }
  }
  .text {
    margin-top: 32px;
    text-align: center;

    h3 {
      ${h242428};
      margin-bottom: 18px;
    }

    p {
      ${p18};
    }

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      width: 32rem;
      text-align: left;
    }

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      margin-right: 60px;
    }
  }

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    &:nth-of-type(even) {
      .image {
        order: 2;
      }
    }
  }
`;

type Props = {
  title: Node | string,
  list: {
    image: {
      name: string,
      alt: string,
      widths: number[],
    },
    text: Node,
  }[],
  images: AllImages,
};

const AlternatingImageList = ({ title, list, images, ...rest }: Props) => {
  const getImage = useImages(images);
  return (
    <Wrapper {...rest}>
      {title}
      <List role="list">
        {list.map(({ image: { name, alt, widths }, text }, index) => (
          <ListItem key={`AlternatingImageList-${index}`} role="listitem" widthArray={widths}>
            <div className="image">
              <div>
                <ImageLoader image={getImage(name)} alt={alt} />
              </div>
            </div>
            <div className="text">{text}</div>
          </ListItem>
        ))}
      </List>
    </Wrapper>
  );
};

export default AlternatingImageList;
